import Alpine from 'alpinejs';

/**
 * Arama kutusu önerileri — Alpine sürümü (v3-* temaları).
 *
 * `default` / `social` / `catalog` temaları Alpine kullanmıyor; onlar için
 * çıplak JS uygulaması var (`consumer/default/js/modules/search-suggest.js`).
 * İkisi aynı uç noktayı, aynı yanıt şeklini ve aynı sınıf adlarını paylaşıyor.
 *
 * Neden ayrı: ortak bir bileşen yazmak cazip görünüyor ama `default`
 * paketine Alpine eklemek o üç temaya ~40 KB getirirdi. Paylaşılan kısım
 * zaten sunucu yanıtı ve sınıf adları; mantığın gerisi iki ayrı yazım.
 *
 * Bu sürümün farkı: durum (`open`, `active`, `suggestions`) bileşende
 * yaşıyor ve şablon `x-for` ile çiziliyor. Panel HTML'i JS ile
 * birleştirilmiyor, dolayısıyla ürün adı HTML'e gömülmüyor.
 *
 * Panel `x-show` yerine `is-open` sınıfıyla açılıyor; ortak stil dosyası
 * `.ks-suggest`'ı varsayılan olarak gizli tanımlıyor, yani Alpine başlamadan
 * önce de panel görünmez ve `x-cloak`'e gerek kalmıyor.
 *
 * @see resources/assets/consumer/shared/scss/_search-suggest.scss
 * @see resources/assets/consumer/default/js/modules/search-suggest.js
 */
export default function searchSuggest() {
    document.addEventListener('alpine:init', () => {
        // `initialTerm`: arama sayfasında kutu terimle dolu geliyor
        // (`value="{{ $query }}"`). `x-model` girdiyi kendi durumundan
        // doldurduğu için o değeri ayrıca geçmiyoruz; yoksa kutuyu boşaltırdı.
        Alpine.data('searchSuggest', (endpoint, initialTerm = '') => ({
            MIN_LENGTH: 2,
            DEBOUNCE_MS: 200,

            term: initialTerm || '',
            open: false,
            loading: false,

            // Seçili satırın `flat` içindeki sırası; -1 = seçim yok.
            active: -1,

            suggestions: { products: [], categories: [], brands: [] },

            timer: null,
            requestId: 0,

            /**
             * Tüm satırlar, panel sırasıyla: ürün, kategori, marka.
             *
             * `type` satırla birlikte taşınıyor; `hrefFor()` imzayı tek
             * argüman alıyor ve çağıran yerin grubu bilmesi gerekmiyor.
             */
            get entries() {
                const out = [];

                if (this.suggestions.products.length) {
                    this.suggestions.products.forEach((row) => out.push({ row, type: 'product' }));
                }

                if (this.suggestions.categories.length) {
                    this.suggestions.categories.forEach((row) => out.push({ row, type: 'category' }));
                }

                if (this.suggestions.brands.length) {
                    this.suggestions.brands.forEach((row) => out.push({ row, type: 'brand' }));
                }

                return out;
            },

            /**
             * Başlıklı gruplar.
             *
             * Her satır `entries` içindeki **sırasını** da taşıyor (`index`).
             * Şablon klavye seçimini `active === entry.index` ile karşılaştırıyor;
             * indeks burada hesaplanıyor çünkü şablonda `indexOf` çalışmaz —
             * satırlar her render'da yeni nesne olarak üretiliyor, yani referans
             * karşılaştırması hep başarısız olurdu.
             */
            get groups() {
                const titles = {
                    product: this.label('suggest_products', 'Ürünler'),
                    category: this.label('suggest_categories', 'Kategoriler'),
                    brand: this.label('suggest_brands', 'Markalar'),
                };

                const groups = [];

                this.entries.forEach((entry, index) => {
                    let group = groups.find((candidate) => candidate.type === entry.type);

                    if (!group) {
                        group = { title: titles[entry.type], type: entry.type, rows: [] };
                        groups.push(group);
                    }

                    group.rows.push({ row: entry.row, type: entry.type, index });
                });

                return groups;
            },

            init() {
                this.$watch('term', (value) => {
                    const term = (value || '').trim();

                    clearTimeout(this.timer);

                    if (term.length < this.MIN_LENGTH) {
                        // Bekleyen istek geçersiz kılınıyor: sayaç
                        // artırılmazsa eski yanıt paneli sonradan açabilir.
                        this.requestId++;
                        this.close();
                        return;
                    }

                    this.timer = setTimeout(() => this.query(term), this.DEBOUNCE_MS);
                });
            },

            label(key, fallback) {
                return (typeof translates !== 'undefined' && translates[key]) || fallback;
            },

            /**
             * Arama sayfasının adresi.
             *
             * `x-ref="form"` olmadan bu `window.location.pathname`'e düşüyor ve
             * `/kategori/...` sayfasındayken marka/kategori önerileri yanlış adres
             * üretiyordu.
             */
            searchBase() {
                const form = this.$refs.form;

                return form ? form.getAttribute('action') || window.location.pathname : window.location.pathname;
            },

            /**
             * "Tüm sonuçları gör" satırının adresi.
             *
             * Aynı `searchBase()` + `query` biçimini kullanıyor, yani anahtar
             * sıralaması önerilerle birebir aynı. Tek fark: daraltılmamış
             * sonuç listesi, yani önerideki `brand`/`category` yok.
             */
            allResultsHref() {
                const term = this.term.trim();

                return term ? `${this.searchBase()}?query=${encodeURIComponent(term)}` : '';
            },

            /**
             * Marka ve kategori önerileri arama sayfasının adres çubuğu
             * seçimlerine gidiyor; vitrindeki kutular da tam olarak bu
             * adresleri üretiyor, yani öneri ile kutu aynı sonucu verir.
             *
             * Değer **kimlik**, isim değil. Arama sayfası seçimi
             * `ctype_digit` ile kabul ediyor (`DefaultTheme`); isim
             * gönderilirse sessizce yok sayılıyor ve kutu tıklaması
             * daraltılmamış sonuç listesi açıyor.
             */
            hrefFor(entry) {
                if (entry.type === 'brand') {
                    return `${this.searchBase()}?query=${encodeURIComponent(this.term.trim())}&brand=${entry.row.id}`;
                }

                if (entry.type === 'category') {
                    return `${this.searchBase()}?query=${encodeURIComponent(this.term.trim())}&category=${entry.row.id}`;
                }

                return entry.row.url;
            },

            priceFor(row) {
                if (typeof row.price !== 'number' || row.price <= 0) {
                    return '';
                }

                const formatted = Number(row.price).toLocaleString('tr-TR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                });

                return row.currency ? `${formatted} ${row.currency}` : formatted;
            },

            async query(term) {
                if (!endpoint) {
                    return;
                }

                const mine = ++this.requestId;

                this.loading = true;

                // Panel istek sürerken de açık kalıyor, yoksa "Aranıyor..."
                // göstergesi hiç görünmezdi: `open` yalnızca yanıt gelince
                // öneri varsa true oluyordu. Başarısız yanıtta `close()`
                // çağrıldığı için panel yine kapanıyor.
                this.open = true;

                try {
                    const response = await fetch(`${endpoint}?query=${encodeURIComponent(term)}`, {
                        headers: { Accept: 'application/json' },
                    });

                    if (!response.ok) {
                        this.close();
                        return;
                    }

                    const data = await response.json();

                    // Kullanıcı bu arada başka bir terim yazdıysa bu yanıt
                    // eskimiştir; yazılmıyor.
                    if (mine !== this.requestId) {
                        return;
                    }

                    this.suggestions = {
                        products: data.products || [],
                        categories: data.categories || [],
                        brands: data.brands || [],
                    };
                    this.active = -1;
                    this.open = this.entries.length > 0;
                } catch (e) {
                    // Öneri, aramanın çalışmasının ön şartı değil: hata
                    // kullanıcıya gösterilmiyor, kutu kapanıyor.
                    if (mine === this.requestId) {
                        this.close();
                    }
                } finally {
                    if (mine === this.requestId) {
                        this.loading = false;
                    }
                }
            },

            close() {
                this.open = false;
                this.active = -1;
            },

            /**
             * Enter.
             *
             * `x-on:submit.prevent="navigate"` **koşulsuz** `preventDefault`
             * yapıyor; bu yüzden seçim yoksa formu elle gönderiyoruz. Aksi
             * hâlde Enter hiçbir şey yapmazdı.
             */
            navigate() {
                const entry = this.entries[this.active];

                if (this.open && entry) {
                    window.location.href = this.hrefFor(entry);
                    return;
                }

                if (this.$refs.form) {
                    this.$refs.form.submit();
                }
            },

            onKeydown(event) {
                if (event.key === 'Escape') {
                    this.close();
                    return;
                }

                const total = this.entries.length;

                if (!this.open || !total) {
                    return;
                }

                if (event.key === 'ArrowDown') {
                    event.preventDefault();
                    this.active = (this.active + 1) % total;
                } else if (event.key === 'ArrowUp') {
                    event.preventDefault();
                    this.active = (this.active - 1 + total) % total;
                }
            },
        }));
    });
}
